import { Injectable } from '@angular/core';
import type { GeoJSONSource, MapboxGeoJSONFeature } from 'mapbox-gl';
import { NaturalId } from '../../menu/right-menu/layers-menu/population-menu/population.service';
import { ARBITRARY_CELL, LayerStoreService } from './layer-store.service';
import { MapBoxService } from '../mapbox.service';
import { BehaviorSubject } from 'rxjs';
import { convertLayerIdToCellType } from '../../../shared/util/layerIdToCellType';

export const SELECTED_POLYGON = 'SELECTED_POLYGON';
export const HOVERED_POLYGON = 'HOVERED_POLYGON';

const SELECTED_OUTLINE_COLOR = '#695dff';
// Secondary colour: the cell under the cursor has to read as "clickable"
// without ever being mistaken for the one that is actually selected.
const HOVERED_OUTLINE_COLOR = '#02a4ff';

@Injectable({
  providedIn: 'root',
})
export class SelectedCellService {
  public selectedCellGeoId: BehaviorSubject<NaturalId | null> =
    new BehaviorSubject<NaturalId | null>(null);

  private selectedFeature: MapboxGeoJSONFeature | null = null;
  private hoveredFeature: MapboxGeoJSONFeature | null = null;

  constructor(
    private layerStoreService: LayerStoreService,
    private mapboxService: MapBoxService
  ) {
    this.layerStoreService.activeLevel.subscribe((level) => {
      this.onLevelChange();
    });
  }

  public selectCell(feature: MapboxGeoJSONFeature): void {
    // deselect previous cell
    this.deselectCell();
    this.selectedFeature = feature;
    this.selectedCellGeoId.next(feature.properties!.external_id);

    if (feature.layer.id !== ARBITRARY_CELL) {
      // The hovered cell is almost always the one just clicked, and two
      // outlines on the same polygon only muddy the colour.
      this.removeOutline(HOVERED_POLYGON);
      this.drawOutline(SELECTED_POLYGON, feature, SELECTED_OUTLINE_COLOR);
    }
  }

  private onLevelChange() {
    this.deselectCell();
    this.unhoverFeature();
  }

  public isCellSelected(): boolean {
    return this.selectedCellGeoId.getValue() !== null;
  }

  public getSelectedCellGeoId(): NaturalId | null {
    return this.selectedCellGeoId.getValue();
  }

  public getSelectedFeature(): MapboxGeoJSONFeature | null {
    return this.selectedFeature;
  }

  public getCellType(): string | undefined {
    const layerId =
      this.getSelectedFeature()?.layer.id ??
      this.layerStoreService.activeLevel.getValue();

    return convertLayerIdToCellType(layerId);
  }

  /**
   * Same outline the click draws, in the secondary colour: the map answers the
   * cursor instead of waiting to be clicked.
   */
  public hoverFeature(feature: MapboxGeoJSONFeature): void {
    const externalId = feature.properties?.['external_id'];

    if (this.hoveredFeature?.properties?.['external_id'] === externalId) return;

    this.hoveredFeature = feature;

    if (externalId && externalId === this.selectedCellGeoId.getValue()) {
      this.removeOutline(HOVERED_POLYGON);
      return;
    }

    this.drawOutline(HOVERED_POLYGON, feature, HOVERED_OUTLINE_COLOR);
  }

  public unhoverFeature(): void {
    this.hoveredFeature = null;
    this.removeOutline(HOVERED_POLYGON);
  }

  public getHoveredFeature(): MapboxGeoJSONFeature | null {
    return this.hoveredFeature;
  }

  public deselectCell() {
    this.selectedCellGeoId.next(null);
    this.selectedFeature = null;

    this.removeOutline(SELECTED_POLYGON);
  }

  /**
   * The layer outlives a single feature: moving between cells only feeds the
   * source new data, which is the cheap half of this compared with dropping
   * and re-adding a layer on every mouse move.
   */
  private drawOutline(
    id: string,
    feature: MapboxGeoJSONFeature,
    color: string
  ): void {
    const map = this.mapboxService.map;

    try {
      const source = map.getSource(id) as GeoJSONSource | undefined;

      if (source) {
        source.setData(feature);
        return;
      }

      map.addLayer({
        id,
        type: 'line',
        source: {
          type: 'geojson',
          data: feature,
        },
        paint: {
          'line-color': color,
          'line-width': 2,
        },
      });
    } catch {
      // Map is gone (see removeOutline) - there is nothing left to outline.
    }
  }

  private removeOutline(id: string): void {
    // Use try catch as when we move from main map we use map.remove() which doesnt cast "map" to falsy, so it exists but
    // calling a method from this object leads to error which created error in "if (map && map.getLayer)" as map counted truthy but map.getLayer is err
    let layerExists = false;
    try {
      layerExists = !!this.mapboxService.map.getLayer(id);
    } catch (e) {
      layerExists = false;
    }

    if (layerExists) {
      this.mapboxService.map.removeLayer(id);
      this.mapboxService.map.removeSource(id);
    }
  }
}
